import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import * as m from 'motion/react-m';

interface AnimatedChatTitleProps {
  title: string;
  className?: string;
}

/**
 * Chat title that crossfades when the title text changes — the moment the
 * async LLM-generated title (streamed in via the `chat_metadata` chunk)
 * replaces the instant heuristic title the chat was created with.
 *
 * `initial={false}` keeps the first render static so list mounts and
 * virtualised re-mounts never animate; only a genuine title change does.
 * Reduced-motion users get a plain opacity fade (no transform).
 */
export function AnimatedChatTitle({ title, className }: AnimatedChatTitleProps) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <TypographyP2 className={cn('relative min-w-0 flex-1 truncate', className)}>
      <AnimatePresence initial={false} mode="popLayout">
        <m.span
          key={title}
          className="inline-block max-w-full truncate align-top"
          initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{
            opacity: 0,
            y: prefersReducedMotion ? 0 : -4,
            transition: { duration: 0.15, ease: 'easeOut' },
          }}
          transition={{
            y: { type: 'spring', stiffness: 550, damping: 40 },
            opacity: { duration: 0.2, ease: 'easeOut' },
          }}
        >
          {title}
        </m.span>
      </AnimatePresence>
    </TypographyP2>
  );
}
